import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, X, GitCompare } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "../ui/sheet";
import { api, fmtPrice } from "../../lib/api";
import { useFavorites } from "../../hooks/useFavorites";

export default function SavedDrawer({ light = false }) {
  const { ids, count, toggle } = useFavorites();
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState([]);
  const navigate = useNavigate();

  useEffect(() => { if (open) api.get("/properties").then((r) => setAll(r.data)); }, [open]);
  const saved = all.filter((p) => ids.includes(p.id));

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="saved-trigger" className={`relative flex items-center gap-2 text-[13px] uppercase tracking-[0.14em] link-underline ${light ? "text-[#FAF9F6]" : "text-[#0A1128]"}`}>
          <Heart size={15} className={count > 0 ? "fill-[var(--gold)] text-[var(--gold)]" : ""} />
          Saved
          {count > 0 && <span data-testid="saved-count" className="ml-1 bg-[var(--gold)] text-[#0A1128] text-[10px] px-1.5 py-0.5 rounded-full">{count}</span>}
        </button>
      </SheetTrigger>
      <SheetContent className="bg-[#FAF9F6] w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-serif text-3xl text-[#0A1128]">Saved Homes</SheetTitle>
          <SheetDescription className="text-[var(--slate)]">Your shortlisted residences, kept on this device.</SheetDescription>
        </SheetHeader>
        {saved.length === 0 ? (
          <div className="mt-16 text-center text-[var(--slate)]" data-testid="saved-empty">
            <Heart size={32} className="mx-auto mb-4 text-[#0A1128]/20" />
            <p>No saved homes yet. Tap the heart on any listing to keep it here.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-6">
            {saved.length >= 2 && (
              <button data-testid="compare-cta" onClick={() => { setOpen(false); navigate("/compare"); }}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#0A1128] text-[#FAF9F6] text-sm uppercase tracking-[0.14em] hover:bg-[#1a2342] transition-colors duration-300">
                <GitCompare size={16} /> Compare {Math.min(saved.length, 3)} Homes
              </button>
            )}
            {saved.map((p) => (
              <div key={p.id} data-testid={`saved-item-${p.id}`} className="flex gap-4 border-b border-[#0A1128]/10 pb-6">
                <Link to={`/property/${p.id}`} onClick={() => setOpen(false)} className="hover-img w-28 h-24 shrink-0">
                  <img src={p.images?.[0]} alt={p.title} className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1">
                  <Link to={`/property/${p.id}`} onClick={() => setOpen(false)}>
                    <h3 className="font-serif text-lg text-[#0A1128] leading-tight">{p.title}</h3>
                  </Link>
                  <p className="text-xs text-[var(--slate)] mt-0.5">{p.location}</p>
                  <p className="font-serif text-[var(--gold)] mt-1">{fmtPrice(p.price)}</p>
                </div>
                <button data-testid={`saved-remove-${p.id}`} onClick={() => toggle(p.id)} className="self-start text-[#0A1128]/40 hover:text-[#0A1128] transition-colors"><X size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
